import '../../../shared/styles/Orchestrator.css';
import { memo, useMemo } from 'react';

// Color palettes (summer / autumn)
const SUMMER_LEAF_COLORS = ['#2e7d32', '#388e3c', '#43a047', '#1b5e20', '#4caf50', '#2b8a3e'];
const AUTUMN_LEAF_COLORS = ['#d84315', '#ff9800', '#ffb300', '#f57c00', '#8d6e63', '#c62828', '#ff5722', '#bf360c'];
const EFFECT_PALETTES = {
  leavesSummer: SUMMER_LEAF_COLORS,
  leavesAutumn: AUTUMN_LEAF_COLORS,
};

const SMOKE_VIDEO_SRC = 'https://video.wixstatic.com/video/d47472_58cce06729c54ccb935886c4b3647274/1080p/mp4/file.mp4';

const randomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];

function buildLeafStyle(palette) {
  const base = randomItem(palette);
  const accent = randomItem(palette);
  const left = Math.random() * 100;
  const scale = 0.4 + Math.random() * 0.9;
  const rotate = Math.random() * 360;
  // Slow leaves: quarter speed (4x original duration)
  const duration = (6 + Math.random() * 10) * 4;
  const delay = Math.random() * 32;
  const fallVariant = Math.floor(Math.random() * 3) + 1;
  return {
    style: {
      '--leaf-duration': `${duration}s`,
      '--leaf-delay': `${delay}s`,
      '--leaf-rotate': `${rotate}deg`,
      '--leaf-fall-fn': `leafFall${fallVariant}`,
      left: `${left}%`,
      animation: `var(--leaf-fall-fn) var(--leaf-duration) linear var(--leaf-delay) infinite`,
      transform: `rotate(${rotate}deg) scale(${scale}) skew(15deg)`,
      background: `linear-gradient(to bottom right, ${base}, ${accent})`,
      opacity: 0.5 + Math.random() * 0.4,
    },
  };
}

function buildSnowStyle() {
  const startX = Math.random() * 110 - 5;
  const drift = (Math.random() * 40 - 20).toFixed(2);
  const scale = (0.25 + Math.random() * 1.2).toFixed(2);
  const opacity = (0.25 + Math.random() * 0.6).toFixed(2);
  const duration = (30 + Math.random() * 12).toFixed(2); // slower fall: 14s-26s
  const delay = (Math.random() * 10).toFixed(2);
  const blur = (Math.random() * 1.8).toFixed(2);
  return {
    style: {
      left: `${startX}%`,
      '--snow-drift': `${drift}vw`,
      '--snow-scale': scale,
      '--snow-opacity': opacity,
      '--snow-duration': `${duration}s`,
      '--snow-delay': `${delay}s`,
      '--snow-blur': `${blur}px`,
    },
  };
}

function buildRainStyle() {
  const startX = (Math.random() * 130).toFixed(2);
  const length = (8 + Math.random() * 14).toFixed(2);
  const opacity = (0.3 + Math.random() * 0.5).toFixed(2);
  const duration = (0.4 + Math.random() * 0.35).toFixed(2);
  const delay = (Math.random() * 3).toFixed(2);
  return {
    style: {
      left: `${startX}%`,
      '--rain-length': `${length}px`,
      '--rain-opacity': opacity,
      '--rain-duration': `${duration}s`,
      '--rain-delay': `${delay}s`,
    },
  };
}

function EffectRenderer({ effect }) {
  if (!effect) return null;

  const leaves = useMemo(() => {
    if (effect !== 'leavesSummer' && effect !== 'leavesAutumn') return [];
    const palette = EFFECT_PALETTES[effect] || SUMMER_LEAF_COLORS;
    const count = 180; // density
    return new Array(count).fill(0).map((_, i) => ({ id: i, ...buildLeafStyle(palette) }));
  }, [effect]);

  const snow = useMemo(() => {
    if (effect !== 'blizzard') return [];
    const count = 140;
    return new Array(count).fill(0).map((_, i) => ({ id: i, ...buildSnowStyle() }));
  }, [effect]);

  const rain = useMemo(() => {
    if (effect !== 'storm') return [];
    const count = 200;
    return new Array(count).fill(0).map((_, i) => ({ id: i, ...buildRainStyle() }));
  }, [effect]);

  if (effect === 'leavesSummer' || effect === 'leavesAutumn') {
    return (
      <div className='effect_overlay effect_leaves_dynamic'>
        {leaves.map((leaf) => (
          <div key={leaf.id} className='dyn_leaf' style={leaf.style} />
        ))}
      </div>
    );
  }

  if (effect === 'blizzard') {
    return (
      <div className='effect_overlay effect_blizzard'>
        <div className='blizzard_fog' />
        {snow.map((flake) => (
          <div key={flake.id} className='blizzard_flake' style={flake.style} />
        ))}
      </div>
    );
  }

  if (effect === 'smog') {
    return (
      <div className='effect_overlay effect_smog'>
        <video
          className='effect_smoke_video'
          autoPlay
          loop
          muted
          playsInline
          aria-hidden='true'
        >
          <source src={SMOKE_VIDEO_SRC} type='video/mp4' />
        </video>
        <div className='effect_smog_tint' />
        <div className='effect_smog_soften' />
      </div>
    );
  }

  if (effect === 'fireflies') {
    return (
      <div className='effect_overlay effect_fireflies'>
        {new Array(25).fill(0).map((_, i) => {
          const colors = ['#f3d8edff','#6cdbecff','#aaf7e6ff','#f1e4e4ff','#ffd600'];
          const color = colors[i % colors.length];
          const left = Math.random()*100;
          const top = Math.random()*100;
          const moveDelay = Math.random()*25;
          const moveDur = 20 + Math.random()*30; // 20s - 50s
          const driftDur = 8 + Math.random()*10; // 8s - 18s
          const driftDelay = Math.random()*12;
          const flashDur = 4 + Math.random()*5; // 4s - 9s
          const flashDelay = Math.random()*7;
          const moveKeys = ['moveA','moveB','moveC','moveD'];
          const moveKey = moveKeys[Math.floor(Math.random()*moveKeys.length)];
          return (
            <div
              key={i}
              className='firefly'
              style={{
                '--fly-color': color,
                left: left + '%',
                top: top + '%',
                '--move-key': moveKey,
                '--move-delay': `${moveDelay}s`,
                '--move-dur': `${moveDur}s`,
                '--drift-dur': `${driftDur}s`,
                '--drift-delay': `${driftDelay}s`,
                '--flash-dur': `${flashDur}s`,
                '--flash-delay': `${flashDelay}s`,
              }}
            />
          );
        })}
      </div>
    );
  }

  if (effect === 'enchantedFog') {
    return (
      <div className='effect_overlay effect_enchanted_fog'>
        <video
          className='effect_smoke_video'
          autoPlay
          loop
          muted
          playsInline
          aria-hidden='true'
        >
          <source src={SMOKE_VIDEO_SRC} type='video/mp4' />
        </video>
        <div className='effect_enchanted_fog_tint' />
        <div className='effect_enchanted_fog_soften' />
      </div>
    );
  }

  if (effect === 'enchantedFogFireflies') {
    return (
      <>
        <div className='effect_overlay effect_enchanted_fog'>
          <video
            className='effect_smoke_video'
            autoPlay
            loop
            muted
            playsInline
            aria-hidden='true'
          >
            <source src={SMOKE_VIDEO_SRC} type='video/mp4' />
          </video>
          <div className='effect_enchanted_fog_tint' />
          <div className='effect_enchanted_fog_soften' />
        </div>
        <div className='effect_overlay effect_fireflies'>
          {new Array(25).fill(0).map((_, i) => {
          const colors = ['#f3d8edff','#6cdbecff','#aaf7e6ff','#f1e4e4ff','#ffd600'];
            const color = colors[(i + 2) % colors.length];
            const left = Math.random()*100;
            const top = Math.random()*100;
            const moveDelay = Math.random()*25;
            const moveDur = 20 + Math.random()*30;
            const driftDur = 8 + Math.random()*10;
            const driftDelay = Math.random()*12;
            const flashDur = 4 + Math.random()*5;
            const flashDelay = Math.random()*7;
            const moveKeys = ['moveA','moveB','moveC','moveD'];
            const moveKey = moveKeys[Math.floor(Math.random()*moveKeys.length)];
            return (
              <div
                key={i}
                className='firefly'
                style={{
                  '--fly-color': color,
                  left: left + '%',
                  top: top + '%',
                  '--move-key': moveKey,
                  '--move-delay': `${moveDelay}s`,
                  '--move-dur': `${moveDur}s`,
                  '--drift-dur': `${driftDur}s`,
                  '--drift-delay': `${driftDelay}s`,
                  '--flash-dur': `${flashDur}s`,
                  '--flash-delay': `${flashDelay}s`,
                }}
              />
            );
          })}
        </div>
      </>
    );
  }

  if (effect === 'storm') {
    return (
      <div className='effect_overlay effect_storm'>
        <div className='storm_tint' />
        {rain.map((drop) => (
          <div key={drop.id} className='storm_drop' style={drop.style} />
        ))}
        <div className='storm_thunder storm_thunder_a' />
        <div className='storm_thunder storm_thunder_b' />
        <div className='storm_thunder storm_thunder_c' />
      </div>
    );
  }

  return null;
}

export default memo(EffectRenderer, (prev, next) => prev.effect === next.effect);
